import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { collection, addDoc, doc, updateDoc } from 'firebase/firestore';
import { LuBriefcase, LuContact, LuLoaderCircle, LuSave, LuUserPen, LuUserPlus } from 'react-icons/lu';
import { db } from '../../config/firebaseConfig';
import { Field, SelectInput, TextArea, TextInput } from '../../components/form/FormControls';
import Button from '../../components/ui/Button';
import GlassIcon from '../../components/ui/GlassIcon';
import Modal from '../../components/ui/Modal';
import useExternalPersonnelText from './externalPersonnelText';
import { FIELD_KEYS, FORM_FIELDS, STATUS_VALUES, statusOf } from './externalPersonnelColumns';

const FORM_ID = 'external-personnel-form';

const SECTIONS = [
  { key: 'contact', icon: LuContact, fields: ['Adı', 'Soyadı', 'E-Posta', 'Telefon', 'Adres'] },
  { key: 'work', icon: LuBriefcase, fields: ['Kurum', 'Ünvan', 'Uzmanlık', 'Ana Bilim Dalı', 'Personel Tipi', 'Durum', 'Bilgi'] },
];
const INPUT_TYPES = { 'E-Posta': 'email', 'Telefon': 'tel' };
const WIDE = ['Adres', 'Bilgi'];

const toForm = (personnel) => {
  const data = Object.fromEntries(FORM_FIELDS.map((key) => [key, personnel?.[key] == null ? '' : String(personnel[key])]));
  // Durum her zaman 'aktif' / 'inaktif' olarak yazılır (eski düzenleme formu 'Aktif' / 'İnaktif' yazıyordu)
  data['Durum'] = statusOf(personnel?.['Durum']) || 'aktif';
  return data;
};

/**
 * Yeni / düzenle harici personel (the old page had two copies of this form).
 * Every field is required, as before; the warning lists the empty ones.
 */
const ExternalPersonnelFormModal = ({ isOpen, personnel, onClose, onSaved }) => {
  const { language, label, fieldLabel, statusLabel } = useExternalPersonnelText();
  const isEditing = Boolean(personnel);
  const [formData, setFormData] = useState(() => toForm(personnel));
  const [fieldErrors, setFieldErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const handleChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (fieldErrors[name] && value && value.toString().trim() !== '') setFieldErrors((prev) => ({ ...prev, [name]: false }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const errors = {};
    FORM_FIELDS.forEach((key) => { errors[key] = !(formData[key] && formData[key].toString().trim() !== ''); });
    setFieldErrors(errors);
    const emptyFields = FORM_FIELDS.filter((key) => errors[key]);
    if (emptyFields.length > 0) {
      toast.warn(label('form.missing', { fields: emptyFields.map(fieldLabel).join(', ') }));
      const firstErrorField = document.getElementById(`ext-${FIELD_KEYS[emptyFields[0]]}`);
      if (firstErrorField) {
        firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
        firstErrorField.focus();
      }
      return;
    }

    setSaving(true);
    try {
      if (isEditing) {
        await updateDoc(doc(db, 'ExternalPersonnels', personnel.id), formData);
        toast.success(label('toast.updated'));
      } else {
        await addDoc(collection(db, 'ExternalPersonnels'), formData);
        toast.success(label('toast.added'));
      }
      onSaved();
      onClose();
    } catch (error) {
      console.error('Error saving external personnel:', error);
      toast.error(isEditing ? label('toast.updateError') : label('toast.addError'));
    } finally {
      setSaving(false);
    }
  };

  const renderControl = (key) => {
    const id = `ext-${FIELD_KEYS[key]}`;
    const error = fieldErrors[key] ? label('form.required') : undefined;
    if (key === 'Durum') {
      return (
        <SelectInput
          id={id}
          value={formData[key]}
          onChange={(event) => handleChange(key, event.target.value)}
          options={STATUS_VALUES.map((value) => ({ value, label: statusLabel(value) }))}
          error={error}
        />
      );
    }
    if (key === 'Adres') {
      return <TextArea id={id} rows={2} value={formData[key]} onChange={(event) => handleChange(key, event.target.value)} error={error} />;
    }
    return <TextInput id={id} type={INPUT_TYPES[key] || 'text'} value={formData[key]} onChange={(event) => handleChange(key, event.target.value)} error={error} />;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="form"
      dismissible={!saving}
      title={isEditing ? label('form.editTitle') : label('form.newTitle')}
      subtitle={isEditing ? `${personnel['Adı'] || ''} ${personnel['Soyadı'] || ''}`.trim() : label('form.subtitle')}
      closeLabel={label('common.cancel')}
      lang={language}
      icon={(
        <GlassIcon gradient="tw-from-fuchsia-400 tw-to-purple-600 tw-shadow-fuchsia-500/30" size="xs">
          {isEditing ? <LuUserPen aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" /> : <LuUserPlus aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />}
        </GlassIcon>
      )}
      footer={(
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>{label('common.cancel')}</Button>
          <Button variant="primary" type="submit" form={FORM_ID} disabled={saving}>
            {saving ? <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin" /> : <LuSave aria-hidden="true" className="tw-h-4 tw-w-4" />}
            {label('common.save')}
          </Button>
        </>
      )}
    >
      <form id={FORM_ID} onSubmit={handleSubmit} noValidate className="tw-space-y-5">
        {SECTIONS.map(({ key, icon: Icon, fields }) => (
          <fieldset key={key} className="tw-rounded-2xl tw-border tw-border-slate-200/80 tw-bg-white/60 tw-p-4 dark:tw-border-white/10 dark:tw-bg-white/[0.02]">
            <legend className="tw-flex tw-items-center tw-gap-2 tw-px-1.5 tw-text-sm tw-font-semibold tw-text-slate-800 dark:tw-text-slate-100">
              <Icon aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-fuchsia-500" />
              {label(`form.sections.${key}`)}
            </legend>
            <div className="tw-grid tw-gap-3 sm:tw-grid-cols-2">
              {fields.map((field) => (
                <Field
                  key={field}
                  label={fieldLabel(field)}
                  htmlFor={`ext-${FIELD_KEYS[field]}`}
                  required
                  error={fieldErrors[field] ? label('form.required') : undefined}
                  className={WIDE.includes(field) ? 'sm:tw-col-span-2' : ''}
                >
                  {renderControl(field)}
                </Field>
              ))}
            </div>
          </fieldset>
        ))}
      </form>
    </Modal>
  );
};

ExternalPersonnelFormModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  personnel: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};

export default ExternalPersonnelFormModal;
